import { useEffect, useState } from 'react';

import { useFetch, useRequest } from '../../hooks';
import { isDrivingPromptType } from './shared';
import { getLlmDefaultModel, testLlmModel } from '../../api';
import type { Item, FormType } from './usePrompts';

type TestStatus =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'ok' }
  | { state: 'error'; message: string };

export const useModelMeta = (
  initialValues: Item | undefined,
  values: FormType | null,
  handleChange: React.Dispatch<React.SetStateAction<Partial<FormType>>>
) => {
  const [defaultModel, setDefaultModel] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<TestStatus>({ state: 'idle' });

  const isCreate = !initialValues;
  const isDriving = isDrivingPromptType(values?.prompt_type);
  const modelValue = (values?.model || '').trim();
  const showFallbackHint = isDriving && !modelValue && !isCreate;

  const { loading } = useFetch(() => getLlmDefaultModel(), [isCreate], {
    onSuccess: ({ model }) => {
      setDefaultModel(model);
      if (!isCreate || !model) return;

      handleChange(prev => (prev?.model ? prev : { ...prev, model }));
    },
  });

  useEffect(() => {
    setTestStatus({ state: 'idle' });
  }, [modelValue]);

  const [handleTestModel, testing] = useRequest(
    () => {
      setTestStatus({ state: 'pending' });
      return testLlmModel(modelValue);
    },
    {
      deps: [modelValue],
      onSuccess: result => {
        if (result.ok) return setTestStatus({ state: 'ok' });

        setTestStatus({
          state: 'error',
          message: result.error || 'Model rejected by OpenRouter',
        });
      },
      onError: err => {
        const message = err instanceof Error ? err.message : 'Test failed';
        setTestStatus({ state: 'error', message });
      },
    }
  );

  return {
    loadingMeta: loading || testing,

    isDriving,
    modelValue,
    testStatus,
    defaultModel,
    showFallbackHint,
    handleTestModel,
  };
};
